/**
 * iter-156sf — Safari-safe auth token store.
 *
 * The app authenticates with HttpOnly session cookies, but Safari's
 * "Prevent Cross-Site Tracking" (ITP) blocks the cross-site auth cookie,
 * logging users out immediately. As a fallback we ALSO keep the JWTs in
 * localStorage and send the access token via an `Authorization: Bearer`
 * header (which Safari does not strip). Chrome keeps using the more
 * secure HttpOnly cookies; the header is simply redundant there.
 */
const ACCESS_KEY = 'sx_access_token';
const REFRESH_KEY = 'sx_refresh_token';
// "Remember this device for 30 days" — same ITP problem as the auth cookie:
// on a custom frontend domain the `td_token` cookie is third-party and gets
// dropped, so the user was asked for a 2FA code on every login. We keep a copy
// here and replay it as an `X-Trusted-Device` header on login.
const TRUSTED_DEVICE_KEY = 'sx_trusted_device';

export function getAccessToken() {
  try { return localStorage.getItem(ACCESS_KEY) || ''; } catch { return ''; }
}

export function getRefreshToken() {
  try { return localStorage.getItem(REFRESH_KEY) || ''; } catch { return ''; }
}

export function setTokens(data) {
  try {
    if (data?.access_token) localStorage.setItem(ACCESS_KEY, data.access_token);
    if (data?.refresh_token) localStorage.setItem(REFRESH_KEY, data.refresh_token);
  } catch { /* storage disabled (private mode quota) — cookies still try */ }
}

export function clearTokens() {
  try {
    localStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(REFRESH_KEY);
    localStorage.removeItem(ACTIVE_TENANT_KEY);
  } catch { /* noop */ }
}

// Platform-admin "view as" tenant. The server also sets an `active_tenant_id`
// cookie, but on a custom domain that cookie can be dropped exactly like the
// auth cookie — the request then authenticates via Bearer yet silently scopes
// to the admin's HOME company (writes 404 / land in the wrong tenant). We keep
// a copy and send it as `X-Active-Tenant` (server re-checks access).
const ACTIVE_TENANT_KEY = 'sx_active_tenant';
export function getActiveTenant() {
  try { return localStorage.getItem(ACTIVE_TENANT_KEY) || ''; } catch { return ''; }
}
export function setActiveTenant(tenantId) {
  try {
    if (tenantId) localStorage.setItem(ACTIVE_TENANT_KEY, tenantId);
    else localStorage.removeItem(ACTIVE_TENANT_KEY);
  } catch { /* noop */ }
}

// Deliberately NOT cleared by clearTokens(): signing out is not the same as
// forgetting the device. Only "Forget all devices" in Security wipes this.
export function getTrustedDevice() {
  try { return localStorage.getItem(TRUSTED_DEVICE_KEY) || ''; } catch { return ''; }
}
export function setTrustedDevice(token) {
  try { if (token) localStorage.setItem(TRUSTED_DEVICE_KEY, token); } catch { /* noop */ }
}
export function clearTrustedDevice() {
  try { localStorage.removeItem(TRUSTED_DEVICE_KEY); } catch { /* noop */ }
}
